import type {
  ApiFlowsExecutionsResponseType,
  ApiFlowsResponseType,
  FlowsType,
} from "@/types/entities/flows";
import axios from "@/utils/axios";
import type { ApiNode } from "@/workflow_api/types/workflow";

import {
  keepPreviousData,
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

// Types for calculated field data structures
interface CalculatedInput {
  type: string;
  calculations?: CalculatedFieldCalculation[];
}

interface CalculatedFieldCalculation {
  inputs?: CalculatedFieldCalculationInput[];
}

interface CalculatedFieldCalculationInput {
  source_connections?: SourceConnection[];
}

interface SourceConnection {
  field?: {
    id: string;
    name: string;
    type: string;
  };
}

interface GetFlowsFilters {
  page?: number;
  perPage?: number;
  search?: string;
  status?: string; // Added status filter
  order_by?: string;
  parent?: string;
}

type MainFlowResponse = {
  id: string;
  parent_flow_id: string | null;
  duplicated_origin_id: string | null;
  original_flow_id: string | null;
  name: string;
  version: number;
  version_number: number;
  version_description: string | null;
  type: string;
  status: string;
  review_status: string;
  max_time_extensions: number;
  is_duplicating: boolean;
  is_main_flow: boolean;
  is_editable: boolean;
  active_formalities_count: number;
  process_id: string;
  created_by: string;
  editing: boolean;
  last_checked_at: string | null;
  updated_at: string;
  created_at: string;
  revisions?: {
    id: string;
    flow_id: string;
    status: "PENDING" | "IN_PROCESS" | "APPROVED" | "DENIED";
    created_at: string;
    updated_at: string;
  }[];
};

type MainFlowsResponse = {
  data: MainFlowResponse[];
  links: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
  meta: {
    current_page: number;
    from: number;
    last_page: number;
    path: string;
    per_page: number;
    to: number;
    total: number;
  };
};

type UpdateFlowData = {
  name?: string;
  parent_flow_id?: string;
  status?: "DRAFT" | "PUBLISHED" | "DEPRECATED" | "IN_PROCESS";
  editing?: boolean;
  review_status?:
    | "NOT_STARTED"
    | "PENDING"
    | "IN_PROCESS"
    | "APPROVED"
    | "DENIED";
};

export const useGetFlowsByProcessId = (
  processId: string | undefined,
  params: GetFlowsFilters,
  interval = 0,
  enabled?: boolean,
) => {
  const queryParams = {
    page: params.page || 1,
    per_page: params.perPage || 10,
    filter: {
      process_id: processId,
      ...(params.search && { search: params.search }),
      ...(params.status && { status: params.status }), // Include status filter
      ...(params.parent && { parent: params.parent }),
    },
    ...(params.order_by && { order_by: params.order_by }),
  };

  return useQuery({
    queryKey: ["flows", queryParams],
    queryFn: async (): Promise<ApiFlowsResponseType> => {
      const response = await axios.get(`/admin/v1/flows`, {
        params: {
          ...queryParams,
          relationships: ["childFlows"].join(","),
        },
      });
      return response.data;
    },
    ...(interval > 0 && { refetchInterval: interval }),
    enabled: !!processId && enabled, // Only run the query if processId is provided and enabled is true
    placeholderData: keepPreviousData,
  });
};

export const useGetFlowsByProcessIdAsInfinite = (
  processId: string | undefined,
  params: Omit<GetFlowsFilters, "page">,
) => {
  const queryParams = {
    per_page: params.perPage || 10,
    filter: {
      process_id: processId,
      ...(params.search && { search: params.search }),
      ...(params.status && { status: params.status }), // Include status filter
    },
    ...(params.order_by && { order_by: params.order_by }),
  };
  return useInfiniteQuery({
    queryKey: ["flows-infinite", queryParams],
    queryFn: async ({ pageParam = 1 }): Promise<ApiFlowsResponseType> => {
      const response = await axios.get(`/admin/v1/flows`, {
        params: {
          ...queryParams,
          page: pageParam,
          relationships: ["childFlows", "renderableNodes"].join(","),
        },
      });
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      const { current_page, last_page } = (
        lastPage as {
          meta: { current_page: number; last_page: number };
        }
      ).meta;
      if (current_page < last_page) {
        return current_page + 1;
      }
    },
    enabled: !!processId, // Only run the query if processId is provided
    initialPageParam: 1,
  });
};

export const useGetMainFlowsByProcessId = (processId?: string) => {
  return useQuery({
    queryKey: ["main-flows", processId],
    queryFn: async (): Promise<MainFlowsResponse> => {
      const response = await axios.get(`/admin/v1/flows`, {
        params: {
          per_page: 30,
          filter: {
            process_id: processId,
            main_flow: true,
          },
          order_by: "!created_at",
          relationships: "revisions,revisions.comments",
        },
      });
      return response.data;
    },
    enabled: !!processId,
  });
};

export const useStoreFlow = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: FlowsType) => {
      const response = await axios.post("/admin/v1/flows", data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["flows"],
      });
    },
    onError: (error) => {
      // Handle error here, e.g., show a notification
      console.error("Error creating flow:", error);
    },
  });
};

export const useUpdateFlow = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    //mutationFn: async (data: Partial<FlowsType>) => {
    mutationFn: async (data: Partial<UpdateFlowData>) => {
      const response = await axios.put(`/admin/v1/flows/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-flow-detail", id],
      });
    },
    onError: (error) => {
      // Handle error here, e.g., show a notification
      console.error("Error updating flow:", error);
    },
  });
};

export const useUpdateFlowStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { id: string; status: FlowsType["status"] }) => {
      const response = await axios.patch(`/admin/v1/flows/${params.id}`, {
        status: params.status,
      });
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["admin-flow-detail", variables.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["flows"],
      });
      queryClient.invalidateQueries({
        queryKey: ["main-flows"],
      });
    },
    onError: (error) => {
      // Handle error here, e.g., show a notification
      console.error("Error updating flow status:", error);
    },
  });
};

// export const usePublishFlow = () => {
//   const queryClient = useQueryClient();
//   return useMutation({
//     mutationFn: async (flowId: string) => {
//       const response = await axios.put(`/admin/v1/flows/${flowId}`, {
//         status: "PUBLISHED",
//       });
//       return response.data;
//     },
//     onSuccess: (_, flowId) => {
//       queryClient.invalidateQueries({ queryKey: ["main-flows"] });
//       queryClient.invalidateQueries({ queryKey: ["flows"] });
//       queryClient.invalidateQueries({
//         queryKey: ["admin-flow-detail", flowId],
//       });
//     },
//   });
// };

export const useArchiveFlow = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (flowId: string) => {
      const response = await axios.put(`/admin/v1/flows/${flowId}`, {
        status: "DEPRECATED",
      });
      return response.data;
    },
    onSuccess: (_, flowId) => {
      queryClient.invalidateQueries({ queryKey: ["main-flows"] });
      queryClient.invalidateQueries({ queryKey: ["flows"] });
      queryClient.invalidateQueries({
        queryKey: ["admin-flow-detail", flowId],
      });
    },
  });
};

const getFlow = async (
  id: string,
  includeNodes: boolean = true,
): Promise<{
  data: ApiFlowsResponseType["data"][number] & { nodes: ApiNode[] };
}> => {
  const response = await axios.get(
    `/admin/v1/flows/${id}?relationships=${[
      ...(includeNodes
        ? [
            "nodes",
            "nodes.edge",
            "nodes.edges",
            "nodes.targetConnections",
            "nodes.inputs",
            "nodes.inputs.testNodeInput",
            "nodes.inputs.sourceConnections.field",
            "nodes.inputs.conditions",
            "nodes.inputs.calculations",
            "nodes.inputs.calculations.inputs.sourceConnections.field",
            "nodes.inputs.calculations.inputs.testNodeInput",
            "nodes.inputs.calculations.inputs.targetConnections.field",
            "nodes.inputs.conditions.input.targetConnections.field",
            "nodes.inputs.conditions.input.sourceConnections.field",
            "nodes.inputs.conditions.input.testNodeInput",
            "nodes.inputs.conditions.optionalInput.testNodeInput",
            "nodes.inputs.conditions.optionalInput",
            "nodes.inputs.conditions.optionalInput.references",
            "nodes.inputs.conditions.optionalInput.sourceConnections.field",
            "nodes.inputs.conditions.optionalInput.targetConnections.field",
            "nodes.inputs.conditions.input.references",
            "nodes.inputs.documents.inputs",
          ]
        : []),
      "revisions",
      "revisions.comments",
    ].join(",")}`,
  );
  return response.data;
};

export const useGetFlow = (id: string, includeNodes: boolean = true) => {
  return useQuery({
    queryKey: ["admin-flow-detail", id],
    enabled: !!id,
    staleTime: 500,
    queryFn: async (): Promise<{
      data: ApiFlowsResponseType["data"][number] & { nodes: ApiNode[] };
    }> => {
      const flow = await getFlow(id, includeNodes);
      return flow;
    },
  });
};

export const useDeleteFlow = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (flowId: string) => {
      const response = await axios.delete(`/admin/v1/flows/${flowId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["flows"],
      });
      queryClient.invalidateQueries({
        queryKey: ["main-flows"],
      });
    },
    onError: (error) => {
      // Handle error here, e.g., show a notification
      console.error("Error deleting flow:", error);
    },
  });
};

/**
 * Obtiene los datos completos de un nodo con todas las relaciones necesarias
 * para la deserialización del campo calculado
 */
const getNodeWithCalculatedFieldData = async (
  flowId: string,
  nodeId: string,
): Promise<ApiNode | null> => {
  console.log(
    `🔍 [getNodeWithCalculatedFieldData] Obteniendo nodo ${nodeId} del flow ${flowId}`,
  );

  const response = await axios.get(
    `/admin/v1/flows/${flowId}?relationships=${[
      "nodes",
      "nodes.edge",
      "nodes.edges",
      "nodes.targetConnections",
      "nodes.inputs",
      "nodes.inputs.conditions",
      "nodes.inputs.calculations",
      "nodes.inputs.calculations.inputs.sourceConnections.field",
      "nodes.inputs.conditions.input.sourceConnections.field",
      "nodes.inputs.calculations.inputs.targetConnections.field",
      "nodes.inputs.conditions.input.targetConnections.field",
      "nodes.inputs.conditions.optionalInput",
      "nodes.inputs.conditions.optionalInput.sourceConnections.field",
      "nodes.inputs.conditions.optionalInput.targetConnections.field",
    ].join(",")}`,
  );

  console.log(
    `📦 [getNodeWithCalculatedFieldData] Respuesta completa del API:`,
    response.data,
  );

  const flowData = response.data;
  const node = flowData.data.nodes?.find((n: ApiNode) => n.id === nodeId);

  console.log(`🎯 [getNodeWithCalculatedFieldData] Nodo encontrado:`, node);

  if (node && node.inputs) {
    console.log(
      `📝 [getNodeWithCalculatedFieldData] Inputs del nodo:`,
      node.inputs,
    );

    // Buscar el input de tipo CALCULATED
    const calculatedInput = node.inputs.find(
      (input: CalculatedInput) => input.type === "CALCULATED",
    );
    if (calculatedInput) {
      console.log(
        `🧮 [getNodeWithCalculatedFieldData] Input calculado encontrado:`,
        calculatedInput,
      );

      if (
        calculatedInput.calculations &&
        calculatedInput.calculations.length > 0
      ) {
        console.log(
          `⚙️ [getNodeWithCalculatedFieldData] Calculations:`,
          calculatedInput.calculations,
        );

        calculatedInput.calculations.forEach(
          (calc: CalculatedFieldCalculation, index: number) => {
            console.log(
              `🔢 [getNodeWithCalculatedFieldData] Calculation ${index}:`,
              calc,
            );

            if (calc.inputs) {
              console.log(
                `📊 [getNodeWithCalculatedFieldData] Inputs del calculation ${index}:`,
                calc.inputs,
              );

              calc.inputs.forEach(
                (
                  input: CalculatedFieldCalculationInput,
                  inputIndex: number,
                ) => {
                  console.log(
                    `🔗 [getNodeWithCalculatedFieldData] Input ${inputIndex} de calculation ${index}:`,
                    input,
                  );

                  if (
                    input.source_connections &&
                    input.source_connections.length > 0
                  ) {
                    console.log(
                      `🌐 [getNodeWithCalculatedFieldData] Source connections del input ${inputIndex}:`,
                      input.source_connections,
                    );

                    input.source_connections.forEach(
                      (conn: SourceConnection, connIndex: number) => {
                        console.log(
                          `🔌 [getNodeWithCalculatedFieldData] Connection ${connIndex}:`,
                          conn,
                        );

                        if (conn.field) {
                          console.log(
                            `🏷️ [getNodeWithCalculatedFieldData] Field del connection ${connIndex}:`,
                            conn.field,
                          );
                        }
                      },
                    );
                  }
                },
              );
            }
          },
        );
      }
    }
  }

  return node || null;
};

/**
 * Obtiene los datos completos de un nodo de interoperabilidad con todas las relaciones necesarias
 */
const getInteroperabilityNodeData = async (
  flowId: string,
  nodeId: string,
): Promise<ApiNode | null> => {
  console.log(
    `🔍 [getInteroperabilityNodeData] Obteniendo nodo ${nodeId} del flow ${flowId}`,
  );

  const response = await axios.get(
    `/admin/v1/flows/${flowId}?relationships=${[
      "nodes",
      "nodes.edge",
      "nodes.targetConnections",
      "nodes.inputs",
      "nodes.inputs.conditions",
      "nodes.inputs.calculations",
      "nodes.inputs.calculations.inputs.sourceConnections.field",
      "nodes.inputs.conditions.input.sourceConnections.field",
      "nodes.inputs.calculations.inputs.targetConnections.field",
      "nodes.inputs.conditions.input.targetConnections.field",
      "nodes.inputs.conditions.optionalInput",
      "nodes.inputs.conditions.optionalInput.sourceConnections.field",
      "nodes.inputs.conditions.optionalInput.targetConnections.field",
    ].join(",")}`,
  );

  console.log(
    `📦 [getInteroperabilityNodeData] Respuesta completa del API:`,
    response.data,
  );

  const flowData = response.data;
  const node = flowData.data.nodes?.find((n: ApiNode) => n.id === nodeId);

  console.log(`🎯 [getInteroperabilityNodeData] Nodo encontrado:`, node);

  return node || null;
};

export const useGetNodeWithCalculatedFieldData = () => {
  return useMutation({
    mutationFn: async ({
      flowId,
      nodeId,
    }: {
      flowId: string;
      nodeId: string;
    }): Promise<ApiNode | null> => {
      return await getNodeWithCalculatedFieldData(flowId, nodeId);
    },
    onError: (error) => {
      console.error("Error obtaining node with calculated field data:", error);
    },
  });
};

export const useGetInteroperabilityNodeData = () => {
  return useMutation({
    mutationFn: async ({
      flowId,
      nodeId,
    }: {
      flowId: string;
      nodeId: string;
    }): Promise<ApiNode | null> => {
      return await getInteroperabilityNodeData(flowId, nodeId);
    },
    onError: (error) => {
      console.error("Error obtaining node with interoperability data:", error);
    },
  });
};

export const createFlowDetailsBreadcrumb = (
  queryClient: QueryClient,
  id: string,
) => {
  return async () => {
    const flowId = id;
    try {
      const queryKey = ["admin-flow-detail", flowId];

      const flow = await queryClient.ensureQueryData({
        queryKey,
        queryFn: async () => getFlow(flowId),
        staleTime: 5 * 60 * 1000,
      });

      return { data: flow.data };
    } catch (error) {
      console.log("Error fetching admin-flow-detail for breadcrumb:", error);
      return {
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  };
};

export const useGetFlowExecutionsAsInfinite = (params: {
  flow_id: string;
  status?: string;
  context?: string;
  applicant_id?: string;
  created_at?: string;
  search?: string;
}) => {
  return useInfiniteQuery({
    queryKey: ["flow-executions-infinite", params],
    queryFn: async ({
      pageParam = 1,
    }): Promise<ApiFlowsExecutionsResponseType> => {
      const response = await axios.get(`/admin/v1/flows/executions`, {
        params: {
          page: pageParam,
          per_page: 10,
          relationships: "formalityRequests",
          filter: {
            flow_id: params.flow_id,
            ...(params.status && { status: params.status }),
            ...(params.context && { context: params.context }),
            ...(params.applicant_id && { applicant_id: params.applicant_id }),
            ...(params.created_at && { created_at: params.created_at }),
            ...(params.search && { search: params.search }),
          },
        },
      });
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      const { current_page, last_page } = (
        lastPage as {
          meta: { current_page: number; last_page: number };
        }
      ).meta;
      if (current_page < last_page) {
        return current_page + 1;
      }
    },
    initialPageParam: 1,
  });
};

export const useGetFlowExecutions = (params: {
  flow_id: string;
  status?: string;
  context?: string;
  applicant_id?: string;
  created_at?: string;
  search?: string;
  page?: number;
}) => {
  return useQuery({
    queryKey: ["flow-executions", params],
    enabled: !!params.flow_id,
    staleTime: 500,
    queryFn: async (): Promise<ApiFlowsExecutionsResponseType> => {
      const response = await axios.get(`/admin/v1/flows/executions`, {
        params: {
          per_page: 20,
          relationships: "formalityRequests",
          filter: {
            flow_id: params.flow_id,
            ...(params.status && { status: params.status }),
            ...(params.context && { context: params.context }),
            ...(params.applicant_id && { applicant_id: params.applicant_id }),
            ...(params.created_at && { created_at: params.created_at }),
            ...(params.search && { search: params.search }),
          },
        },
      });
      return response.data;
    },
  });
};

export const useGetExecution = (id: string, refreshInterval: number = 0) => {
  return useQuery({
    queryKey: ["flow-execution", id],
    enabled: !!id,
    staleTime: 500,
    queryFn: async (): Promise<{
      data: ApiFlowsExecutionsResponseType["data"][number];
    }> => {
      const response = await axios.get(`/admin/v1/flows/executions/${id}`, {
        params: {
          relationships: [
            "nodeExecutions",
            "nodeExecutions.formAssignments",
            "nodeExecutions.formAssignments.revisions",
            "nodeExecutions.formAssignments.filledForm",
            "nodeExecutions.userFormAssignments",
            "nodeExecutions.userFormAssignments.filledForm",
            "nodeExecutions.userFormAssignments.form",
            "nodeExecutions.formAssignments.form",
            "nodeExecutions.inputs",
            "nodeExecutions.outputs",
            "nodeExecutions.node.inputs",
            "nodeExecutions.node.targetConnections",
            "nodeExecutions.inputs.conditions.input",
            "nodeExecutions.inputs.conditions.optionalInput",
            "nodeExecutions.inputs.calculations.inputs",
            "nodeExecutions.paymentRequest",
            "formalityRequests",
            "formalityRequests.applicant",
            "formalityRequests.groups",
            "formalityRequests.assignedTo",
            "formalityRequests.assignedBy",
            "formalityRequests.process",
          ].join(","),
        },
      });
      return response.data;
    },
    ...(refreshInterval > 0 && { refetchInterval: refreshInterval }),
  });
};

export const useDuplicateFlow = (flowId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      name,
      version_description,
      versioning,
    }: {
      name: string;
      version_description?: string;
      versioning?: boolean;
    }) => {
      const response = await axios.post(`/admin/v1/flows/${flowId}/duplicate`, {
        name,
        version_description,
        versioning,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["flows"],
      });
      queryClient.invalidateQueries({
        queryKey: ["main-flows"],
      });
    },
    onError: (error) => {
      // Handle error here, e.g., show a notification
      console.error("Error duplicating flow:", error);
    },
  });
};

export const usePerformFlowConnectionsCheck = (flowId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await axios.post(`/admin/v1/flows/${flowId}/check`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-flow-detail", flowId],
      });
    },
  });
};

export const useSendFlowToApproval = (flowId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await axios.post(`/admin/v1/flows/${flowId}/revision`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-flow-detail", flowId],
      });
      queryClient.invalidateQueries({
        queryKey: ["main-flows"],
      });
      queryClient.invalidateQueries({
        queryKey: ["flows"],
      });
    },
  });
};

export const useUpdateFlowRevision = (flowId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      revisionId: string;
      action: "DENIED" | "APPROVED" | "IN_PROCESS";
      comments?: string[];
    }) => {
      const response = await axios.patch(
        `/admin/v1/flows/revision/${params.revisionId}`,
        {
          status: params.action,
          comments: params.comments || [],
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-flow-detail", flowId],
      });
    },
  });
};

export const useGetFlows = (params: {
  page?: number;
  perPage?: number;
  search?: string;
  status?: string;
  order_by?: string;
}) => {
  const queryParams = {
    page: params.page || 1,
    per_page: params.perPage || 10,
    ...(params.search && { search: params.search }),
    ...(params.status && { status: params.status }),
    ...(params.order_by && { order_by: params.order_by }),
  };
  return useQuery({
    queryKey: ["api-flows", params],
    queryFn: async (): Promise<ApiFlowsResponseType> => {
      const response = await axios.get(`/admin/v1/flows`, {
        params: queryParams,
      });
      return response.data;
    },
    placeholderData: keepPreviousData,
    staleTime: 1000 * 30, // 30 segundos (opcional)
  });
};

export const useGetFlowsAsInfinite = (params: {
  perPage?: number;
  search?: string;
  status?: string;
  order_by?: string;
  parent?: string;
  process_id?: string;
}) => {
  const queryParams = {
    per_page: params.perPage || 20,
    filter: {
      ...(params.process_id && { process_id: params.process_id }),
      ...(params.search && { search: params.search }),
      ...(params.status && { status: params.status }),
      ...(params.parent && { parent: params.parent }),
    },
    ...(params.order_by && { order_by: params.order_by }),
  };
  return useInfiniteQuery({
    queryKey: ["api-flows-infinite", params],
    queryFn: async ({ pageParam = 1 }): Promise<ApiFlowsResponseType> => {
      const response = await axios.get(`/admin/v1/flows`, {
        params: {
          ...queryParams,
          page: pageParam,
        },
      });
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      const { current_page, last_page } = (
        lastPage as {
          meta: { current_page: number; last_page: number };
        }
      ).meta;
      if (current_page < last_page) {
        return current_page + 1;
      }
    },
    initialPageParam: 1,
    staleTime: 1000 * 30,
  });
};
